import React, { useState, useEffect } from "react";
import { FiCheck, FiX, FiChevronDown, FiChevronUp, FiCopy, FiEye } from "react-icons/fi";
import { 
  RiGovernmentLine,
  RiBuildingLine,
  RiMapPinLine,
  RiTeamLine,
  RiUserStarLine,
  RiFlagLine,
  RiVipCrownLine,
  RiUserSettingsLine,
  RiShieldKeyholeLine,
  RiMegaphoneLine,
  RiChatPollLine,
  RiBarChartBoxLine,
  RiSettingsLine,
  RiUserAddLine,
  RiUserUnfollowLine,
  RiUserVoiceLine,
  RiUserSharedLine,
  RiUserReceivedLine
} from "react-icons/ri";

const AdminRolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // API Base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.tinubu-ambode.org";

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current user role from localStorage or context
  useEffect(() => {
    const getCurrentUserInfo = () => {
      try {
        const userData = localStorage.getItem("userData") || 
                        sessionStorage.getItem("userData") ||
                        localStorage.getItem("user") ||
                        sessionStorage.getItem("user");
        
        if (userData) {
          const user = JSON.parse(userData);
          console.log("👤 Current user data:", user);
          
          const role = user.role || user.roles?.[0];
          setCurrentUserRole(role);
          
          console.log("🔐 Current user role:", role);
          return role;
        }
      } catch (error) {
        console.error("Error getting current user info:", error);
      }
      return null;
    };

    getCurrentUserInfo();
  }, []);

  // Get auth token
  const getAuthToken = () => {
    try {
      const token = localStorage.getItem("authToken") || 
                   localStorage.getItem("token") ||
                   sessionStorage.getItem("authToken") ||
                   sessionStorage.getItem("token");
      
      if (!token) {
        console.warn("No authentication token found");
        return null;
      }
      
      return token;
    } catch (error) {
      console.error("Error retrieving auth token:", error);
      return null;
    }
  };

  // Filter roles based on user permissions
  const filterRolesByUserPermission = (roles) => {
    if (!currentUserRole) return roles;
    
    // Super admin can see everything
    if (currentUserRole === 'super-admin') {
      return roles;
    }
    
    // State admin can see everything except super-admin
    if (currentUserRole === 'state-admin') {
      return roles.filter(role => role.name !== 'super-admin');
    }
    
    // SD admin cannot see super-admin and state-admin roles
    if (currentUserRole === 'sd-admin') {
      return roles.filter(role => 
        role.name !== 'super-admin' && 
        role.name !== 'state-admin'
      );
    }
    
    // FC admin cannot see super-admin, state-admin, and sd-admin roles
    if (currentUserRole === 'fc-admin') {
      return roles.filter(role => 
        role.name !== 'super-admin' && 
        role.name !== 'state-admin' &&
        role.name !== 'sd-admin'
      );
    }
    
    // LGA admin cannot see higher level roles
    if (currentUserRole === 'lga-admin') {
      return roles.filter(role => 
        !['super-admin', 'state-admin', 'sd-admin', 'fc-admin'].includes(role.name)
      );
    }
    
    // LCDA admin can only see lower level roles
    if (currentUserRole === 'lcda-admin') {
      return roles.filter(role => 
        !['super-admin', 'state-admin', 'sd-admin', 'fc-admin', 'lga-admin'].includes(role.name)
      );
    }
    
    // Ward admin can only see polling-unit-admin and member roles
    if (currentUserRole === 'ward-admin') {
      return roles.filter(role => 
        ['polling-unit-admin', 'member'].includes(role.name)
      );
    }
    
    // Polling unit admin can only see member role
    if (currentUserRole === 'polling-unit-admin') {
      return roles.filter(role => role.name === 'member');
    }
    
    // Default: return all roles except super-admin for non-super admins
    return roles.filter(role => role.name !== 'super-admin');
  };

  // Fetch roles from API
  const fetchRoles = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      console.log("🌐 Fetching roles from:", `${API_BASE_URL}/api/v1/admin/roles`);

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/roles`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
      });

      console.log("📡 API Response status:", response.status);

      if (response.status === 401) {
        // Clear invalid token
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("token");
        throw new Error("Authentication failed. Please log in again.");
      }

      if (response.status === 403) {
        throw new Error("Access denied. You don't have permission to view roles.");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ API Response data:", result);
      
      if (result.success || result.status === "success") {
        const allRoles = result.data || result.roles || [];
        // Apply role-based filtering
        const filteredRoles = filterRolesByUserPermission(allRoles);
        setRoles(filteredRoles);
        setFilteredRoles(filteredRoles);
      } else {
        throw new Error(result.message || "Failed to fetch roles");
      }
    } catch (err) {
      console.error("❌ Error fetching roles:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [currentUserRole]);

  // Filter roles based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredRoles(roles);
    } else {
      const filtered = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.permissions && role.permissions.some(permission => 
          permission.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
      setFilteredRoles(filtered);
    }
  }, [searchTerm, roles]);

  // Toggle role expansion - only one can be open at a time
  const toggleRoleExpansion = (roleId) => {
    if (expandedRole === roleId) {
      setExpandedRole(null);
    } else {
      setExpandedRole(roleId);
    }
  };

  // Format role name for display
  const formatRoleName = (roleName) => {
    return roleName.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Group permissions by category
  const groupPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
      return {};
    }

    const groups = {
      "Member Management": [],
      "Broadcast Announcements": [],
      "Broadcast Requests": [],
      "Admin Management": [],
      "Group Chat": [],
      "Reports": [],
      "System Management": [],
      "Other": []
    };

    permissions.forEach(permission => {
      if (permission.includes('member')) {
        groups["Member Management"].push(permission);
      } else if (permission.includes('broadcast-announcement')) {
        groups["Broadcast Announcements"].push(permission);
      } else if (permission.includes('broadcast-request')) {
        groups["Broadcast Requests"].push(permission);
      } else if (permission.includes('admin') || permission.includes('create-')) {
        groups["Admin Management"].push(permission);
      } else if (permission.includes('group-chat')) {
        groups["Group Chat"].push(permission);
      } else if (permission.includes('report') || permission.includes('view-')) {
        groups["Reports"].push(permission);
      } else if (permission.includes('manage-') || permission.includes('system') || permission.includes('upload') || permission.includes('theme')) {
        groups["System Management"].push(permission);
      } else {
        groups["Other"].push(permission);
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  };

  // Format permission for display
  const formatPermission = (permission) => {
    return permission.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Copy permissions to clipboard
  const copyPermissionsToClipboard = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
      alert("No permissions to copy");
      return;
    }
    
    const text = permissions.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here instead of alert
      alert("Permissions copied to clipboard!");
    }).catch(err => {
      console.error("Failed to copy permissions:", err);
      alert("Failed to copy permissions to clipboard");
    });
  };

  // View permission details
  const viewPermissionDetails = (permission) => {
    setSelectedPermission(permission);
    setShowPermissionModal(true);
  };

  // Get politics-related icon for each role
  const getRoleIcon = (roleName) => {
    const iconMap = {
      'super-admin': RiVipCrownLine, // Highest authority
      'state-admin': RiGovernmentLine, // State government
      'sd-admin': RiMapPinLine, // Senatorial district
      'fc-admin': RiUserStarLine, // Federal constituency - elected representative
      'lga-admin': RiBuildingLine, // Local government building
      'lcda-admin': RiBuildingLine, // Local council development area
      'ward-admin': RiFlagLine, // Ward level - political territory
      'polling-unit-admin': RiUserVoiceLine, // Polling unit - voter level
      'member': RiTeamLine // General member/constituent
    };
    
    const IconComponent = iconMap[roleName] || RiTeamLine;
    return <IconComponent />;
  };

  // Get politics-related icon for permission categories
  const getCategoryIcon = (category) => {
    const iconMap = {
      "Member Management": RiUserSettingsLine, // Managing people
      "Broadcast Announcements": RiMegaphoneLine, // Political announcements
      "Broadcast Requests": RiChatPollLine, // Communication and polling
      "Admin Management": RiUserStarLine, // Administrative leadership
      "Group Chat": RiTeamLine, // Group discussions
      "Reports": RiBarChartBoxLine, // Political reports and analytics
      "System Management": RiSettingsLine, // System configuration
      "Other": RiFlagLine // General political
    };
    
    const IconComponent = iconMap[category] || RiFlagLine;
    return <IconComponent size={14} />;
  };

  // Get politics-related icon for member management permissions
  const getPermissionIcon = (permission) => {
    if (permission.includes('create-member') || permission.includes('add-member')) {
      return <RiUserAddLine size={12} />;
    } else if (permission.includes('delete-member') || permission.includes('remove-member')) {
      return <RiUserUnfollowLine size={12} />;
    } else if (permission.includes('edit-member')) {
      return <RiUserSettingsLine size={12} />;
    } else if (permission.includes('view-member')) {
      return <RiUserVoiceLine size={12} />;
    } else if (permission.includes('approve-member')) {
      return <RiUserStarLine size={12} />;
    } else if (permission.includes('assign-role')) {
      return <RiUserSharedLine size={12} />;
    } else if (permission.includes('manage-access')) {
      return <RiUserReceivedLine size={12} />;
    }
    return <RiFlagLine size={12} />;
  };

  // Get user-friendly description for current user's view
  const getUserViewDescription = () => {
    if (currentUserRole === 'super-admin') {
      return "Viewing all system roles and permissions";
    } else if (currentUserRole === 'state-admin') {
      return "Viewing all roles except super-admin";
    } else if (currentUserRole === 'sd-admin') {
      return "Viewing roles below senatorial district level";
    } else if (currentUserRole === 'fc-admin') {
      return "Viewing roles below federal constituency level";
    } else if (currentUserRole === 'lga-admin') {
      return "Viewing roles below local government level";
    } else if (currentUserRole === 'lcda-admin') {
      return "Viewing roles below LCDA level";
    } else if (currentUserRole === 'ward-admin') {
      return "Viewing roles below ward level";
    } else if (currentUserRole === 'polling-unit-admin') {
      return "Viewing member role only";
    }
    return "Viewing available roles";
  };

  // Check if current user has access to view this page
  const hasAccessToView = () => {
    const allowedRoles = [
      'super-admin',
      'state-admin',
      'sd-admin', 
      'fc-admin',
      'lga-admin',
      'lcda-admin',
      'ward-admin',
      'polling-unit-admin'
    ];
    return currentUserRole && allowedRoles.includes(currentUserRole);
  };

  // Responsive styles
  const styles = {
    page: {
      padding: isMobile ? '16px 12px' : '24px 20px',
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      overflowX: 'hidden'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50vh',
      textAlign: 'center'
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '16px',
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50vh',
      textAlign: 'center',
      padding: isMobile ? '20px' : '40px'
    },
    errorIcon: {
      fontSize: isMobile ? '36px' : '48px',
      color: '#dc2626',
      marginBottom: '16px',
    },
    retryButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 24px' : '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      marginTop: '16px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: isMobile ? '100%' : 'auto'
    },
    accessDeniedContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
      textAlign: 'center',
      padding: isMobile ? '20px' : '40px'
    },
    accessDeniedIcon: {
      fontSize: isMobile ? '48px' : '64px',
      color: '#dc2626',
      marginBottom: '20px',
    },
    headerSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerIcon: {
      fontSize: isMobile ? '28px' : '32px',
      color: '#3b82f6',
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0',
      lineHeight: 1.2
    },
    pageSubtitle: {
      color: '#6b7280',
      margin: '4px 0 0 0',
      fontSize: isMobile ? '13px' : '14px',
      lineHeight: 1.4
    },
    userViewInfo: {
      backgroundColor: '#dbeafe',
      padding: isMobile ? '10px 12px' : '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      border: '1px solid #bfdbfe',
      fontSize: isMobile ? '12px' : '14px',
      color: '#1e40af',
      fontWeight: '500'
    },
    searchContainer: {
      marginBottom: '20px',
      maxWidth: isMobile ? '100%' : '400px'
    },
    searchInput: {
      width: '100%',
      padding: isMobile ? '12px 16px' : '10px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '16px',
      backgroundColor: 'white',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    rolesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
      gap: isMobile ? '16px' : '20px',
    },
    roleCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      border: '1px solid #f3f4f6'
    },
    roleHeader: {
      padding: isMobile ? '16px' : '20px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
    },
    roleInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px'
    },
    roleNameSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1
    },
    roleIcon: {
      fontSize: isMobile ? '20px' : '24px',
      flexShrink: 0
    },
    roleName: {
      margin: '0',
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      color: '#1f2937',
      lineHeight: 1.3
    },
    rolePermissionsCount: {
      margin: '2px 0 0 0',
      fontSize: isMobile ? '12px' : '14px',
      color: '#6b7280',
    },
    roleActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0
    },
    actionButton: {
      background: 'none',
      border: 'none',
      color: '#6b7280',
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease'
    },
    roleDetails: {
      padding: isMobile ? '16px' : '20px',
    },
    permissionCategory: {
      marginBottom: isMobile ? '16px' : '20px',
    },
    categoryHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
    },
    categoryTitle: {
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      color: '#374151',
      margin: '0',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    permissionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    permissionItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: isMobile ? '8px 10px' : '8px 12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    permissionIcon: {
      color: '#6b7280',
      fontSize: isMobile ? '12px' : '14px',
      flexShrink: 0,
    },
    permissionCheck: {
      color: '#10b981',
      fontSize: isMobile ? '12px' : '14px',
      flexShrink: 0,
    },
    permissionText: {
      fontSize: isMobile ? '12px' : '13px',
      color: '#374151',
      flex: 1,
      lineHeight: 1.4
    },
    viewIcon: {
      color: '#6b7280',
      fontSize: isMobile ? '12px' : '14px',
      flexShrink: 0,
    },
    roleMeta: {
      display: 'flex',
      paddingTop: '12px',
      borderTop: '1px solid #e5e7eb',
      marginTop: '12px'
    },
    metaItem: {
      fontSize: isMobile ? '11px' : '12px',
      color: '#6b7280',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '40px 20px' : '60px 20px',
      textAlign: 'center',
    },
    emptyIcon: {
      fontSize: isMobile ? '36px' : '48px',
      color: '#9ca3af',
      marginBottom: '16px',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '20px',
    },
    modalContainer: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '500px',
      maxHeight: isMobile ? '90vh' : '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isMobile ? '16px' : '20px',
      borderBottom: '1px solid #e5e7eb',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '18px',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '4px',
      borderRadius: '4px',
    },
    modalBody: {
      padding: isMobile ? '16px' : '20px',
    },
    permissionTitle: {
      margin: '0 0 12px 0',
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      color: '#1f2937',
      lineHeight: 1.4
    },
    permissionCode: {
      backgroundColor: '#f8fafc',
      padding: isMobile ? '10px' : '12px',
      borderRadius: '6px',
      marginBottom: '16px',
      fontSize: isMobile ? '12px' : '14px',
      fontFamily: 'monospace',
      color: '#374151',
      overflowX: 'auto'
    },
    permissionInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    infoLabel: {
      fontWeight: '600',
      color: '#374151',
      fontSize: isMobile ? '13px' : '14px'
    },
    infoValue: {
      color: '#6b7280',
      fontSize: isMobile ? '13px' : '14px'
    }
  };

  // Add hover effects for desktop
  if (!isMobile) {
    Object.assign(styles.roleCard, {
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }
    });
    Object.assign(styles.actionButton, {
      ':hover': {
        backgroundColor: '#f3f4f6',
        color: '#374151'
      }
    });
    Object.assign(styles.permissionItem, {
      ':hover': {
        backgroundColor: '#e5e7eb',
        transform: 'translateX(4px)'
      }
    });
  }

  // Check access before rendering content
  if (!hasAccessToView()) {
    return (
      <div style={styles.page}>
        <div style={styles.accessDeniedContainer}>
          <RiShieldKeyholeLine style={styles.accessDeniedIcon} />
          <h2 style={{ color: '#1f2937', margin: '0 0 12px 0', fontSize: isMobile ? '20px' : '24px' }}>
            Access Denied
          </h2>
          <p style={{ color: '#6b7280', margin: '0 0 20px 0', fontSize: isMobile ? '14px' : '16px', maxWidth: '400px' }}>
            You don't have permission to view roles and permissions. This page is only accessible to administrators.
          </p>
          <button 
            onClick={() => window.history.back()} 
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>Loading roles and permissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <FiX style={styles.errorIcon} />
          <h3 style={{ color: '#1f2937', margin: '0 0 8px 0', fontSize: isMobile ? '18px' : '20px' }}>Error Loading Roles</h3>
          <p style={{ color: '#6b7280', margin: '0 0 16px 0', fontSize: isMobile ? '14px' : '16px' }}>{error}</p>
          <button onClick={fetchRoles} style={styles.retryButton}>
            <RiGovernmentLine size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .role-card-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }
          
          .permission-item-hover:hover {
            background-color: #e5e7eb;
            transform: translateX(4px);
          }
          
          .action-btn-hover:hover {
            background-color: #f3f4f6;
            color: #374151;
          }
          
          input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          
          @media (max-width: 480px) {
            .mobile-text-sm {
              font-size: 0.875rem;
            }
          }
        `}
      </style>

      {/* Permission Details Modal */}
      {showPermissionModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPermissionModal(false)}>
          <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', color: '#1f2937' }}>Permission Details</h3>
              <button 
                onClick={() => setShowPermissionModal(false)}
                style={styles.closeButton}
                className="action-btn-hover"
              >
                <FiX />
              </button>
            </div>
            <div style={styles.modalBody}>
              <h4 style={styles.permissionTitle}>
                {selectedPermission ? formatPermission(selectedPermission) : 'Permission Details'}
              </h4>
              <div style={styles.permissionCode}>
                <code>{selectedPermission || 'No permission selected'}</code>
              </div>
              {selectedPermission && (
                <div style={styles.permissionInfo}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Scope:</span>
                    <span style={styles.infoValue}>
                      {selectedPermission.includes('nationwide') ? 'Nationwide' : 
                      selectedPermission.includes('state') ? 'State Level' :
                      selectedPermission.includes('senatorial') ? 'Senatorial District' :
                      selectedPermission.includes('federal') ? 'Federal Constituency' :
                      selectedPermission.includes('local-government') ? 'Local Government' :
                      selectedPermission.includes('lcda') ? 'LCDA' :
                      selectedPermission.includes('ward') ? 'Ward' :
                      selectedPermission.includes('polling-unit') ? 'Polling Unit' : 'General'}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Type:</span>
                    <span style={styles.infoValue}>
                      {selectedPermission.includes('view-') ? 'Read Access' :
                      selectedPermission.includes('create-') ? 'Create Access' :
                      selectedPermission.includes('edit-') ? 'Edit Access' :
                      selectedPermission.includes('delete-') ? 'Delete Access' :
                      selectedPermission.includes('manage-') ? 'Management Access' :
                      selectedPermission.includes('approve-') ? 'Approval Access' :
                      selectedPermission.includes('broadcast-') ? 'Broadcast Access' : 'General Access'}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Module:</span>
                    <span style={styles.infoValue}>
                      {selectedPermission.includes('member') ? 'Member Management' :
                      selectedPermission.includes('broadcast') ? 'Broadcast System' :
                      selectedPermission.includes('admin') ? 'Admin Management' :
                      selectedPermission.includes('group') ? 'Group Chat' :
                      selectedPermission.includes('report') ? 'Reports' :
                      selectedPermission.includes('system') ? 'System Management' : 'General'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerLeft}>
          <RiGovernmentLine style={styles.headerIcon} />
          <div>
            <h1 style={styles.pageTitle}>Admin Roles & Permissions</h1>
            <p style={styles.pageSubtitle}>
              {getUserViewDescription()}
            </p>
          </div>
        </div>
      </div>

      {/* User View Information */}
      {currentUserRole && currentUserRole !== 'super-admin' && (
        <div style={styles.userViewInfo}>
          🔒 <strong>Restricted View:</strong> {getUserViewDescription()}
        </div>
      )}

      {/* Search Input */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search roles or permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Roles Grid */}
      <div style={styles.rolesGrid}>
        {filteredRoles.map((role) => {
          const permissionGroups = groupPermissions(role.permissions);
          const isExpanded = expandedRole === role.id;

          return (
            <div 
              key={role.id} 
              style={styles.roleCard}
              className="role-card-hover"
            >
              {/* Role Header */}
              <div 
                style={styles.roleHeader}
                onClick={() => toggleRoleExpansion(role.id)}
              >
                <div style={styles.roleInfo}>
                  <div style={styles.roleNameSection}>
                    <div style={{
                      ...styles.roleIcon,
                      color: getRoleColor(role.name)
                    }}>
                      {getRoleIcon(role.name)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={styles.roleName}>
                        {formatRoleName(role.name)}
                      </h3>
                      <p style={styles.rolePermissionsCount}>
                        {role.permissions ? role.permissions.length : 0} permissions
                      </p>
                    </div>
                  </div>
                  <div style={styles.roleActions}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyPermissionsToClipboard(role.permissions);
                      }}
                      style={styles.actionButton}
                      className="action-btn-hover"
                      title="Copy all permissions"
                    >
                      <FiCopy size={isMobile ? 16 : 18} />
                    </button>
                    {isExpanded ? 
                      <FiChevronUp size={isMobile ? 18 : 20} /> : 
                      <FiChevronDown size={isMobile ? 18 : 20} />
                    }
                  </div>
                </div>
              </div>

              {/* Role Details - Only show when expanded */}
              {isExpanded && (
                <div style={styles.roleDetails}>
                  {/* Permissions by Category */}
                  {Object.entries(permissionGroups).map(([category, permissions]) => (
                    <div key={category} style={styles.permissionCategory}>
                      <div style={styles.categoryHeader}>
                        {getCategoryIcon(category)}
                        <h4 style={styles.categoryTitle}>{category}</h4>
                      </div>
                      <div style={styles.permissionsList}>
                        {permissions.map((permission, index) => (
                          <div
                            key={index}
                            style={styles.permissionItem}
                            className="permission-item-hover"
                            onClick={() => viewPermissionDetails(permission)}
                            title="Click to view details"
                          >
                            <div style={styles.permissionIcon}>
                              {getPermissionIcon(permission)}
                            </div>
                            <FiCheck style={styles.permissionCheck} />
                            <span style={styles.permissionText}>
                              {formatPermission(permission)}
                            </span>
                            <FiEye style={styles.viewIcon} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Role Meta Information */}
                  <div style={styles.roleMeta}>
                    <span style={styles.metaItem}>
                      Created: {role.created_at ? new Date(role.created_at).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredRoles.length === 0 && (
        <div style={styles.emptyState}>
          <RiTeamLine style={styles.emptyIcon} />
          <h3 style={{ color: '#1f2937', margin: '0 0 8px 0', fontSize: isMobile ? '18px' : '20px' }}>No roles found</h3>
          <p style={{ color: '#6b7280', margin: 0, fontSize: isMobile ? '14px' : '16px' }}>
            {searchTerm ? 'Try adjusting your search terms' : 'No roles available for your access level'}
          </p>
        </div>
      )}
    </div>
  );
};

// Helper function to get role color
const getRoleColor = (roleName) => {
  const colors = {
    'super-admin': '#dc2626', // Red for highest authority
    'state-admin': '#059669', // Green for state level
    'sd-admin': '#7c3aed', // Purple for senatorial
    'fc-admin': '#db2777', // Pink for federal
    'lga-admin': '#ea580c', // Orange for local government
    'lcda-admin': '#0891b2', // Cyan for LCDA
    'ward-admin': '#65a30d', // Lime for ward
    'polling-unit-admin': '#4f46e5', // Indigo for polling unit
    'member': '#6b7280' // Gray for members
  };
  return colors[roleName] || '#6b7280';
};

export default AdminRolesManagement;